import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { terraProxyApiClient } from '../clients/testerClient';
import type { ReportsReturnDto } from '@terra/shared-contracts-reports';

interface UseReportsOptions {
  applicationId?: string;
  enabled?: boolean;
}

export const REPORTS_FETCH_PAGE_SIZE = 100;

export async function fetchAllReportsByApplication({
  applicationId,
  skip = 0,
}: {
  applicationId: string;
  skip?: number;
}): Promise<ReportsReturnDto[]> {
  const page =
    (await terraProxyApiClient.getReportsByApplication(applicationId, {
      skip,
      limit: REPORTS_FETCH_PAGE_SIZE,
    })) ?? [];

  if (page.length < REPORTS_FETCH_PAGE_SIZE) {
    return page;
  }

  const rest = await fetchAllReportsByApplication({
    applicationId,
    skip: skip + page.length,
  });

  return [...page, ...rest];
}

export function useReports({
  applicationId,
  enabled = true,
}: UseReportsOptions) {
  const queryClient = useQueryClient();
  const queryKey = ['reports', applicationId];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      if (!applicationId) {
        return [];
      }

      return fetchAllReportsByApplication({ applicationId });
    },
    enabled: !!applicationId && enabled,
    refetchOnWindowFocus: false,
    staleTime: 30000,
  });

  const deleteReport = useCallback(
    async (reportId: string) => {
      if (!applicationId) return;

      await terraProxyApiClient.deleteReport(reportId, applicationId);

      // Update the cache optimistically
      queryClient.setQueryData<ReportsReturnDto[]>(
        ['reports', applicationId],
        (oldData) =>
          oldData ? oldData.filter((report) => report.id !== reportId) : [],
      );
    },
    [applicationId, queryClient],
  );

  return {
    reports: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    deleteReport,
    isFetching: query.isFetching,
  };
}
